<script lang="ts">
  import prompt from '@/store/prompt';
  import hooksAreaLoadingStore from '@/store/prompt/loading-hooks';
  import type {Video} from '@/types/project';
  import {onMount, createEventDispatcher, onDestroy} from 'svelte';
  import type {ActiveIndex, ClipState, Hook} from '@/types/prompts';
  import LoaderPlus from '../loader/LoaderPlus.svelte';
  import {ProcessState} from '@/types/common';
  import {user} from '@/store';

  export let video: Video;
  export let isOpen: boolean = true;
  let lastIndex: number = 0;
  let activeIndex: ActiveIndex;
  let status: ClipState;
  let items: Array<Hook>;
  let areItemsShortened: Array<Boolean>;
  let display: boolean = false;
  let loaderMessage: string = 'Retrieving Suggested Clips';
  let isDisabled: boolean = false;

  const dispatch = createEventDispatcher();

  // Subscribe to the prompt store
  const unsubscribe = prompt.subscribe(p => {
    items = p.hooks;
    areItemsShortened = items.map(() => true);
    activeIndex = p.activeIndex;
    status = p.status;
    display = p.ready;
  });

  onMount(async () => {
    if ($prompt.hooks.length == 0) {
      hooksAreaLoadingStore.set(true);
      await getPromptStatus();
    }
  });

  onDestroy(() => {
    unsubscribe();
  });

  const getPromptStatus = async () => {
    prompt.getPromptStatus(video.id);
  };

  const regenerateAISC = async (llm: string) => {
    if (status !== 'processing') {
      status = 'processing';
      prompt.regenerateAISC(video.id, llm);
    }
  };

  const setSelections = (hook: Hook, index: number): any => {
    if (activeIndex && activeIndex?.hook?.id != undefined && activeIndex?.hook?.id == hook.id) {
      return;
    }

    prompt.setStatus('selecting');
    prompt.setActiveIndex({hook: hook, modified: false});
    loaderMessage = 'Selecting Suggested Clip';

    lastIndex = index;

    // Dispatch Select Sentence Event
    dispatch('set-selections', {hook: hook, hookIndex: lastIndex});
  };

  const toggleAside = () => {
    isOpen = !isOpen;
  };

  $: if (status === 'processing') {
    isDisabled = true;
  } else {
    isDisabled = false;
  }
</script>

<aside
  class={`${isOpen == true ? 'w-96 px-6 py-3' : 'min-w-max'} 
    relative flex h-full flex-shrink-0 flex-col items-center gap-4 overflow-hidden overflow-y-scroll bg-white bg-opacity-10
`}
>
  {#if isOpen}
    <div>
      <div class="flex items-center justify-between gap-2 px-3 pt-2">
        <h1 class="text-xl font-bold"><span class="sermon">AI</span> Suggested Clips</h1>
        <button on:click={toggleAside} on:keyup={toggleAside}><i class="fa-solid fa-bars"></i></button>
      </div>
      {#if $user.isImpersonated || $user.isAdmin}
        <div class="grid w-full grid-cols-1 gap-2 p-2 md:grid-cols-2">
          <button
            type="button"
            on:click={() => regenerateAISC('claude')}
            class="rounded-lg border-2 border-primary bg-white p-2 text-sm text-primary hover:bg-primary hover:text-white {isDisabled
              ? 'disabled cursor-progress'
              : 'cursor-pointer'}"><i class="fa-solid fa-repeat"></i> Claude</button
          >
          <button
            type="button"
            on:click={() => regenerateAISC('openai')}
            class="rounded-lg border-2 border-primary bg-white p-2 text-sm text-primary hover:bg-primary hover:text-white {isDisabled
              ? 'disabled cursor-progress'
              : 'cursor-pointer'}"><i class="fa-solid fa-repeat"></i> OpenAI</button
          >
        </div>
      {/if}
      {#if $hooksAreaLoadingStore}
        {#if $prompt.status === 'processing'}
          <LoaderPlus status={ProcessState.BUSY} heading={loaderMessage} content="This may take a while" />
        {:else if $prompt.status === 'error'}
          <LoaderPlus status={ProcessState.ERROR} heading="We encountered an error processing your request." />
        {/if}
      {:else if items.length && display == true}
        <div class="flex w-full flex-col gap-4 overflow-y-auto p-2" id="clip-blocks">
          {#each items as hook, i}
            <div
              class={`
                            mx-auto cursor-pointer rounded-xl shadow-md
                            ${activeIndex && activeIndex?.hook?.id == hook.id && activeIndex?.modified == false ? 'border-4 border-primary ' : 'hover:border-primary'} 
                            ${activeIndex && activeIndex?.hook?.id == hook.id && activeIndex?.modified == true ? 'border-4 border-slate-400 ' : ''}`}
              on:click={setSelections(hook, i)}
              on:keyup={setSelections(hook, i)}
              id="clip-{i}"
            >
              <div
                class="flex justify-between gap-2 border-b border-b-zinc-300 px-4 py-2 {`${activeIndex && activeIndex?.hook?.id == hook.id && activeIndex?.modified == false ? 'bg-primary text-zinc-100' : 'rounded-t-xl bg-zinc-100 text-zinc-900'}`}"
              >
                <button class="text-left text-sm font-semibold uppercase">
                  {#if hook.topic}
                    {hook.topic}
                  {:else}
                    Topic
                  {/if}
                </button>
              </div>
              <div
                class="flex gap-2 rounded-b-md p-4 {status === 'selecting' && activeIndex?.hook?.id == hook.id
                  ? '-mt-8 h-full flex-col items-center justify-center'
                  : 'flex-grow flex-wrap'}"
              >
                {#if status === 'selecting' && activeIndex?.hook?.id == hook.id}
                  <LoaderPlus status={ProcessState.BUSY} heading={loaderMessage} />
                {:else}
                  <p
                    class={`font-regular cursor-pointer text-justify text-sm text-zinc-700 ${areItemsShortened[i] ? 'line-clamp-3' : ''}`}
                  >
                    {hook.text}
                  </p>
                  <div class="text-sm text-primary">
                    {#if areItemsShortened[i]}
                      <button
                        class="flex items-center"
                        on:click={event => {
                          event.stopPropagation();
                          areItemsShortened[i] = false;
                        }}
                      >
                        Read more
                        <i class="fa-solid fa-chevron-right ml-2"></i>
                      </button>
                    {:else}
                      <button
                        class="flex items-center"
                        on:click={event => {
                          event.stopPropagation();
                          areItemsShortened[i] = true;
                        }}
                      >
                        Show Less
                        <i class="fa-solid fa-chevron-left ml-2"></i>
                      </button>
                    {/if}
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {:else if display == false}
        <LoaderPlus status={ProcessState.BUSY} heading="Loading Suggested Clips" />
      {/if}
    </div>
  {:else}
    <div class="h-full w-14 bg-white py-4">
      <div class="svelte-1ty35j1 h-full">
        <div class="flex rotate-90 gap-4 px-6">
          <button on:click={toggleAside} on:keyup={toggleAside}
            ><i class="fa-solid fa-bars" aria-hidden="true"></i></button
          >
          <p class="font-bold" style="white-space: nowrap;"><span class="sermon">AI</span> Suggested Clips</p>
        </div>
      </div>
    </div>
  {/if}
</aside>

<style lang="postcss">
  .vertical {
    writing-mode: vertical-rl;
  }
  .sideways {
    text-orientation: sideways;
  }
  .max-lines {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
</style>
